import { isContainSpecialChar } from "../../../../utils/zone.utils";
import { useQueryOriginSource } from "../../../layer/chain/useQueryOriginal";
import useOnLayerAfterSet from "../../../layer/observable/useOnLayerAfterSet";
import useDiscard from "../../../useDiscard";
import useMap from "../../../utils/useMap";
import useWeakMap from "../../../utils/useWeakMap";
import { runExtractValue } from "../../executor";
import { isPropContainKey } from "../../util";
import { reRunWatcher } from "../useWatcher";


const genNodeId = (() => {
    let id = 0;
    return () => `node-${++id}`
})();

const cachingProps = useWeakMap();

const extractPropStr = (props) => {
    props = useQueryOriginSource(props)
    if (!cachingProps.has(props)) {
        const spreadStr = Object.keys(props).filter(str => !isContainSpecialChar(str)).join(',')
        cachingProps.set(props, `const { ${spreadStr} } = props`);
    }
    return cachingProps.get(props);
};


const extractPropByDotNotation = (props, k, collection) => {
    if (k.length === 0) {
        return collection;
    }
    const isContainDot = k.includes('.');
    if (!isContainDot && isPropContainKey(props, k)) {
        collection.add(k);
        return collection;
    } else if (!isContainDot) {
        return collection;
    }
    const list = k.split('.'),
        track = [];
    /** Break if the first accessor is not contain in prop */
    if (!isPropContainKey(props, list[0])) {
        return collection;
    }
    for (let i in list) {
        track.push(list[i]);
        collection.add(track.join('.'));
    }
    return collection;
}

const extractPropKeyFromText = (props, conditionText) => {
    props = useQueryOriginSource(props);
    // Remove special characters
    const regex = /[!@#$%^&*()_+\-=\[\]{};':"\\|,<>\/?]+/g;
    conditionText = conditionText.replace(regex, '');
    const list = conditionText.split(' ').reduce((out, item) => {
        const k = item.trim();
        extractPropByDotNotation(props, k, out);
        return out;
    }, new Set());
    return Array.from(list);
}

const [createComment, queryComment] = (() => {

    const commentPool = useMap();

    const createComment = (uniqueText) => {
        const comment = new Comment(uniqueText);
        commentPool.set(uniqueText, comment);
        return comment;
    }

    const queryComment = (text) => {
        return commentPool.get(text);
    }

    return [createComment, queryComment]
})()


const { useIf, checkNodeIsVisible } = (() => {
    const backupNode = useMap();
    const oldValue = useWeakMap();



    /**
     * Description placeholder
     *
     * @param {string} expression
     * @param {object} props
     * @returns {boolean}
     */
    const runCondition = (condition, props) => {
        const value = runExtractValue(props, condition);
        return Boolean(value);
    }

    /**
     * 
     * @param {Node} node 
     */
    const hideNode = (node) => {
        const parent = node.parentNode;
        if (backupNode.has(node)) {
            return;
        }
        const id = genNodeId();
        const comment = createComment(`rcz-track=${id}`);
        parent.insertBefore(comment, node);
        parent.removeChild(node);
        backupNode.set(node, { node, comment });
    }

    const showNode = (node) => {
        if (backupNode.has(node)) {
            const { comment } = backupNode.get(node);
            const parent = comment.parentNode;
            parent.insertBefore(node, comment);
            parent.removeChild(comment);
            backupNode.delete(node)
        }
    }

    const exec = async (condition, props, node) => {
        const isTrue = runCondition(condition, props);
        const old = oldValue.get(props).get(node);
        if (old === isTrue) {
            return;
        }
        oldValue.get(props).set(node, isTrue);
        if (isTrue) {
            showNode(node);
            reRunWatcher(props, node);
        } else {
            hideNode(node);
        }
    }

    const useIf = (condition, props, node) => {

        const propDeps = extractPropKeyFromText(props, condition);
        oldValue.set(props, useWeakMap());
        oldValue.get(props).set(node, null);

        exec(condition, props, node);
        useOnLayerAfterSet(props, () => {
            exec(condition, props, node);
        }, propDeps)

        useDiscard(props, () => {
            backupNode.clear();
        })

        return Promise.resolve();
    }

    const checkNodeIsVisible = (node) => {
        return false === backupNode.has(node)
    }
    return { useIf, checkNodeIsVisible }
})();

export default useIf;
export { checkNodeIsVisible }